import axios from 'axios';
import { useAuthStore } from '../store/authStore';

const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || '/api',
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
});

// Request interceptor to add auth token
api.interceptors.request.use(
    (config) => {
        const token = useAuthStore.getState().token;
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Las rutas donde un 401 significa "esas credenciales no valen", no
// "tu sesión caducó". Recargar la página en estos casos borraba el aviso de
// error antes de que nadie pudiera leerlo: fallabas el acceso y la pantalla
// se limpiaba sin decirte por qué.
const INTENTOS_DE_ACCESO = /\/auth\/(login|register|google|forgot-password|reset-password)/;

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        const url = error.config?.url || '';

        if (error.response?.status === 401 && !INTENTOS_DE_ACCESO.test(url)) {
            // Sesión caducada o inválida: cerrar y volver al acceso
            useAuthStore.getState().logout();
            window.location.href = '/login';
        }

        return Promise.reject(error);
    }
);

export default api;

// API service functions
export const authAPI = {
    login: (data) => api.post('/auth/login', data),
    register: (data) => api.post('/auth/register', data),
    googleLogin: (credential) => api.post('/auth/google', { credential }),
    logout: () => api.post('/auth/logout'),
    getMe: () => api.get('/auth/me'),
    updateProfile: (data) => api.put('/auth/profile', data),
    forgotPassword: (email, lang = 'es') => api.post('/auth/forgot-password', { email, lang }),
    resetPassword: (data) => api.post('/auth/reset-password', data),
    changePassword: (currentPassword, newPassword) => api.put('/auth/password', { currentPassword, newPassword }),
    deleteAccount: (password) => api.delete('/auth/account', { data: { password } }),
};

export const productsAPI = {
    search: (params) => api.get('/products/search', { params }),
    // Ficha del proveedor para un producto que todavía no se ha importado.
    getDetail: (params) => api.get('/products/detail', { params }),
    getWinning: (params) => api.get('/products/winning', { params }),
    // Busca candidatos a producto ganador en el catálogo del proveedor.
    winningSearch: (data) => api.post('/products/winning/search', data),
    // Evalúa uno a fondo: los tres criterios calculables y los dos que juzga la IA.
    winningEvaluate: (data) => api.post('/products/winning/evaluate', data),
    getById: (id) => api.get(`/products/${id}`),
    analyze: (productId) => api.post(`/products/${productId}/analyze`),
    import: (data) => api.post('/products/import', data),
    getImported: (params) => api.get('/products/imported', { params }),
    // El archivo que importa Shopify desde su propio panel, con los productos
    // que se le digan y solo esos.
    //
    // Llega como binario —`blob`— porque es un archivo, no una respuesta JSON:
    // sin esto axios lo interpretaría como texto y lo rompería. Y va por POST
    // aunque solo lea, porque mil identificadores no caben en una dirección.
    exportarShopify: (productIds) =>
        api.post('/products/imported/export/shopify', { productIds }, { responseType: 'blob' }),
    updateImported: (id, data) => api.put(`/products/imported/${id}`, data),
    deleteImported: (id) => api.delete(`/products/imported/${id}`),
    publishImported: (id) => api.post(`/products/imported/${id}/publish`),
    // Todo lo que eBay exigirá, antes de intentar publicar.
    publishCheck: (id, params = {}) => api.get(`/products/imported/${id}/publish-check`, { params }),
    // Estado real de los anuncios publicados, preguntado a la tienda.
    withdraw: (id) => api.post(`/products/imported/${id}/withdraw`),
    // El cuerpo va vacío, `{}`, y no `null`: esta instancia manda
    // `Content-Type: application/json`, así que axios serializa lo que le des
    // y `JSON.stringify(null)` es la cadena «null». El servidor la recibía,
    // body-parser la rechazaba por no ser JSON válido y devolvía un 400 sin
    // código propio, que la pantalla traducía como «Algo falló en el
    // servidor». Es decir: esta llamada no ha funcionado nunca, ni al abrir
    // Mis Productos ni al pulsar «Comprobar el estado de los anuncios».
    refreshListings: (force = false) =>
        api.post('/products/imported/refresh-listings', {}, { params: force ? { force: 'true' } : {} }),
};

export const aiAPI = {
    analyze: (productId, language) => api.post('/ai/analyze', { productId, language }),
    generateDescription: (productId, style, language) => api.post('/ai/description', { productId, style, language }),
    getPricing: (productId, language) => api.post('/ai/pricing', { productId, language }),
    getMarketing: (productId, language) => api.post('/ai/marketing', { productId, language }),
};

export const storesAPI = {
    getAll: () => api.get('/stores'),
    connect: (data) => api.post('/stores/connect', data),
    getById: (id) => api.get(`/stores/${id}`),
    update: (id, data) => api.put(`/stores/${id}`, data),
    delete: (id) => api.delete(`/stores/${id}`),
    sync: (id) => api.post(`/stores/${id}/sync`),
    ebayAuthUrl: () => api.get('/stores/ebay/auth-url'),
    ebayExchange: (data) => api.post('/stores/ebay/exchange', data),
    // Shopify (Dev Dashboard): se conecta por dominio y el backend obtiene el
    // token con el `client_credentials` de la app. No hace falta pegar token.
    shopifyConnect: (data) => api.post('/stores/shopify/connect', data),
};

export const ordersAPI = {
    getAll: (params) => api.get('/orders', { params }),
    // Trae de la tienda las ventas y las convierte en pedidos.
    sync: () => api.post('/orders/sync'),
    getById: (id) => api.get(`/orders/${id}`),
    fulfill: (id) => api.post(`/orders/${id}/fulfill`),
    updateTracking: (id, data) => api.put(`/orders/${id}/tracking`, data),
    updateStatus: (id, status) => api.put(`/orders/${id}/status`, { status }),
    getTracking: (id) => api.get(`/orders/${id}/track`),
};

export const subscriptionsAPI = {
    getStatus: () => api.get('/subscriptions'),
    getPlans: () => api.get('/subscriptions/plans'),
    checkout: (plan) => api.post('/subscriptions/checkout', { plan }),
    getPortal: () => api.post('/subscriptions/portal'),
    cancel: () => api.post('/subscriptions/cancel'),
    resume: () => api.post('/subscriptions/resume'),
};

export const analyticsAPI = {
    getDashboard: (period) => api.get('/analytics/dashboard', { params: { period } }),
    getSales: (params) => api.get('/analytics/sales', { params }),
    getProducts: (limit) => api.get('/analytics/products', { params: { limit } }),
    getROI: () => api.get('/analytics/roi'),
    // El embudo de la operación y lo que la frena. Contesta «¿qué hago ahora?»
    // y funciona aunque no se haya vendido nunca.
    getEstado: () => api.get('/analytics/estado'),
    // Lo que se espera ganar, calculado sobre los precios y costes ya puestos.
    // Es lo único de Métricas que dice algo antes de la primera venta.
    getRentabilidad: () => api.get('/analytics/rentabilidad'),
    export: (type, format) => api.get('/analytics/export', { params: { type, format } }),
};

export const notificationsAPI = {
    getAll: (params) => api.get('/notifications', { params }),
    markAsRead: (id) => api.put(`/notifications/${id}/read`),
    markAllAsRead: () => api.put('/notifications/read-all'),
    delete: (id) => api.delete(`/notifications/${id}`),
};

export const bulkAPI = {
    bulkImport: (data) => api.post('/bulk/import', data),
    bulkPriceUpdate: (data) => api.post('/bulk/price-update', data),
    bulkStatusChange: (data) => api.post('/bulk/status-change', data),
    getOperations: (params) => api.get('/bulk/operations', { params }),
    getOperation: (id) => api.get(`/bulk/operations/${id}`),
    cancelOperation: (id) => api.delete(`/bulk/operations/${id}`),
};
